import React, { useEffect, useMemo, useState } from 'react';
import { XIcon, SearchIcon, SparklesIcon, CheckIcon } from '../Icons';
import { Skill, SkillService } from '../../services/skillService';

interface SkillModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedSkills: Skill[];
  onSaveSkills: (skills: Skill[]) => void;
}

const SkillModal: React.FC<SkillModalProps> = ({ isOpen, onClose, selectedSkills, onSaveSkills }) => {
  const [search, setSearch] = useState('');
  const [skills, setSkills] = useState<Skill[]>([]);
  const [draftSelection, setDraftSelection] = useState<Skill[]>(selectedSkills);

  useEffect(() => {
    if (!isOpen) return;
    SkillService.getSkills().then(setSkills);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setDraftSelection(selectedSkills);
    setSearch('');
  }, [isOpen, selectedSkills]);

  const filteredSkills = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    if (!normalized) return skills;
    return skills.filter((skill) =>
      skill.name.toLowerCase().includes(normalized) ||
      skill.category.toLowerCase().includes(normalized) ||
      (skill.description || '').toLowerCase().includes(normalized)
    );
  }, [search, skills]);

  const toggleSkill = (skill: Skill) => {
    setDraftSelection((current) =>
      current.some((item) => item.id === skill.id)
        ? current.filter((item) => item.id !== skill.id)
        : [...current, skill]
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose}></div>

      <div className="relative w-full max-w-3xl bg-[#0a0a0c] border border-white/10 rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in duration-300">
        <div className="p-8 border-b border-white/5 flex justify-between items-center bg-white/[0.02]">
          <div>
            <h2 className="text-xl font-black text-white tracking-tight flex items-center gap-3">
              <SparklesIcon className="text-brand-purple w-5 h-5" />
              Agent Skills
            </h2>
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.2em] mt-1">
              Geselecteerde skills worden meegegeven aan chat, lead finder en research.
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-full transition-colors text-slate-400">
            <XIcon />
          </button>
        </div>

        <div className="px-8 py-4 bg-white/[0.01] border-b border-white/5">
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500">
              <SearchIcon />
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Zoek in skills..."
              className="w-full bg-white/5 border border-white/5 rounded-xl pl-12 pr-6 py-3 text-sm text-white outline-none focus:ring-1 focus:ring-brand-purple transition-all"
            />
          </div>
        </div>

        <div className="max-h-[520px] overflow-y-auto custom-scrollbar p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filteredSkills.map((skill) => {
              const isSelected = draftSelection.some((item) => item.id === skill.id);

              return (
                <button
                  key={skill.id}
                  onClick={() => toggleSkill(skill)}
                  className={`p-5 rounded-2xl text-left transition-all border group ${
                    isSelected
                      ? 'bg-brand-purple/10 border-brand-purple/30'
                      : 'bg-white/[0.02] border-white/5 hover:bg-brand-purple/5 hover:border-brand-purple/20'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2 gap-3">
                    <span className="text-[8px] font-black text-brand-purple uppercase tracking-[0.1em]">{skill.category}</span>
                    <span className={`w-5 h-5 rounded-full border flex items-center justify-center ${isSelected ? 'border-brand-purple bg-brand-purple text-white' : 'border-white/10 text-transparent'}`}>
                      <CheckIcon />
                    </span>
                  </div>
                  <h4 className="text-xs font-bold text-white transition-colors">{skill.name}</h4>
                  <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">{skill.description || 'Geen beschrijving beschikbaar.'}</p>
                  <p className="text-[9px] text-slate-600 mt-2 font-mono truncate">{skill.path}</p>
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6 border-t border-white/5 flex items-center justify-between gap-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
            {draftSelection.length} skill{draftSelection.length === 1 ? '' : 's'} actief
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-3 rounded-xl border border-slate-700 text-slate-400 text-[10px] font-bold uppercase tracking-[0.2em] hover:text-white transition-all"
            >
              Sluiten
            </button>
            <button
              onClick={() => {
                onSaveSkills(draftSelection);
                onClose();
              }}
              className="px-8 py-3 rounded-xl bg-[#5249f3] text-white text-[10px] font-bold uppercase tracking-[0.2em] shadow-xl shadow-indigo-600/20 hover:bg-[#6359f5] transition-all"
            >
              Skills Opslaan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillModal;
